import React, { useEffect, useState } from "react";
import "./PasswordReset.css";
// import logo from "/images/LSClogo.png";
import { useParams } from "react-router-dom";
import { accessReset, resetPassword } from "../Services/Api";
import Swal from "sweetalert2";

const PasswordReset = () => {
  const { id } = useParams();
  const [tokenStatus, setTokenStatus] = useState(null);

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false,
  });

  const [passwordStrength, setPasswordStrength] = useState(0);
  const [requirements, setRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  const [passwordMatch, setPasswordMatch] = useState(null);

  const [message, setMessage] = useState({
    type: "",
    text: "",
    show: false,
  });

  // ---------------------------
  // TOKEN VERIFY
  // ---------------------------
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await accessReset(id);
        setTokenStatus(response.tokenStatus); // valid | used | expired
      } catch (err) {
        setTokenStatus("expired");
        console.error("Token verify error:", err.message);
      }
    };
    verifyToken();
  }, [id]);

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // ---------------------------
  // PASSWORD STRENGTH CHECK
  // ---------------------------
  const checkPasswordStrength = (password) => {
    const hasLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

    setRequirements({
      length: hasLength,
      uppercase: hasUppercase,
      lowercase: hasLowercase,
      number: hasNumber,
      special: hasSpecial,
    });

    let strength = 0;
    if (hasLength) strength += 20;
    if (hasUppercase) strength += 20;
    if (hasLowercase) strength += 20;
    if (hasNumber) strength += 20;
    if (hasSpecial) strength += 20;

    setPasswordStrength(strength);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "newPassword") checkPasswordStrength(value);

    if (
      name === "confirmPassword" ||
      (name === "newPassword" && formData.confirmPassword)
    ) {
      const match =
        name === "newPassword"
          ? value === formData.confirmPassword
          : value === formData.newPassword;

      setPasswordMatch(match);
    }
  };

  const getStrengthColor = () => {
    if (passwordStrength < 40) return "#e74c3c";
    if (passwordStrength < 80) return "#f39c12";
    return "#27ae60";
  };

  // ---------------------------
  // SUBMIT HANDLER
  // ---------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({
        type: "error",
        text: "Passwords do not match.",
        show: true,
      });
      return;
    }

    if (passwordStrength < 60) {
      setMessage({
        type: "error",
        text: "Password is too weak.",
        show: true,
      });
      return;
    }

    try {
      const payload = { ...formData, token: id };
      const response = await resetPassword(payload);

      if (response.status === "success") {
        Swal.fire({
          title: "Success!",
          text: "Password Reset Successfully.",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => window.location.reload());
      }
    } catch (err) {
      setMessage({
        type: "error",
        text: "Something went wrong.",
        show: true,
      });
      console.log(err);
    }
  };

  // ---------------------------
  // UI RENDER BASED ON TOKEN STATUS
  // ---------------------------
  const renderLeftContent = () => {
    if (tokenStatus === "expired" || tokenStatus === "invalid") {
      return (
        <div className="pwr-message-box expired">
          <i className="fas fa-exclamation-triangle"></i>
          <h2>Link Expired</h2>
          <p>Sorry, this link has expired. Please request a new one.</p>
        </div>
      );
    }

    if (tokenStatus === "used") {
      return (
        <div className="pwr-message-box used">
          <i className="fas fa-info-circle"></i>
          <h2>Link Already Used</h2>
          <p>Please request a new password reset link.</p>
        </div>
      );
    }

    if (tokenStatus === "valid") {
      return (
        <form className="pwr-form" onSubmit={handleSubmit}>
          {/* NEW PASSWORD */}
          <div className="pwr-form-group">
            <label className="pwr-label">New Password</label>
            <div className="pwr-input-container">
              <input
                type={showPassword.newPassword ? "text" : "password"}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Enter new password"
                className="pwr-input"
              />
              <button
                type="button"
                className="pwr-toggle-password"
                onClick={() => togglePasswordVisibility("newPassword")}
              >
                <i
                  className={`fas ${
                    showPassword.newPassword ? "fa-eye-slash" : "fa-eye"
                  }`}
                ></i>
              </button>
            </div>

            <div className="pwr-password-strength">
              <div
                className="pwr-strength-meter"
                style={{
                  width: `${passwordStrength}%`,
                  backgroundColor: getStrengthColor(),
                }}
              ></div>
            </div>
          </div>

          {/* CONFIRM PASSWORD */}
          <div className="pwr-form-group">
            <label className="pwr-label">Confirm Password</label>
            <div className="pwr-input-container">
              <input
                type={showPassword.confirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm password"
                className="pwr-input"
              />
              <button
                type="button"
                className="pwr-toggle-password"
                onClick={() => togglePasswordVisibility("confirmPassword")}
              >
                <i
                  className={`fas ${
                    showPassword.confirmPassword ? "fa-eye-slash" : "fa-eye"
                  }`}
                ></i>
              </button>
            </div>

            {passwordMatch !== null && formData.confirmPassword && (
              <div
                className={`pwr-password-requirements ${
                  passwordMatch ? "pwr-met" : ""
                }`}
              >
                <i
                  className={`fas ${
                    passwordMatch ? "fa-check-circle" : "fa-exclamation-circle"
                  }`}
                ></i>
                {passwordMatch
                  ? "Passwords match"
                  : "Passwords do not match"}
              </div>
            )}
          </div>

          {/* SUBMIT BUTTON */}
          <button type="submit" className="pwr-submit-btn">
            Reset Password
          </button>

          {/* ALERT MESSAGE */}
          {message.show && (
            <div className={`pwr-message pwr-${message.type}`}>
              <i
                className={`fas ${
                  message.type === "success"
                    ? "fa-check-circle"
                    : "fa-exclamation-circle"
                }`}
              ></i>
              {message.text}
            </div>
          )}

          <div className="pwr-back-to-login">
            <p>
              Remember your password?{" "}
              <a href="/login" className="pwr-link">
                Back to Login
              </a>
            </p>
          </div>
        </form>
      );
    }

    return null;
  };

  return (
    <div className="pwr-container">
      <div className="pwr-wrapper">
        <div className="pwr-form-section">
          <div className="pwr-logo-section">
            <img src="/images/LSClogo.png" alt="Logo" className="pwr-logo-img" />
          </div>

          <div className="pwr-header">
            {tokenStatus === "valid" && (
              <>
                <h1 className="pwr-title">Reset Password</h1>
                <p className="pwr-subtitle">
                  Create a new password for your account
                </p>
              </>
            )}
          </div>

          {renderLeftContent()}
        </div>

        {/* RIGHT SECTION */}
        <div className="pwr-image-section">
          <div className="pwr-icon-container">
            <i className="fas fa-lock"></i>
          </div>
          <div className="pwr-image-content">
            <h2 className="pwr-image-title">Secure Your Account</h2>
            <p className="pwr-image-text">
              Create a strong password to protect your account.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
